// Feature: participant marks a payment as done.
// Screens: 04-member-pay-sheet, 05-member-other-amount, 06-member-paid, 07-member-paid
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import LiquidGlass from 'liquid-glass-react';
import { paymentApi } from '@/entities/payment-claim/api';
import type { RecordPaymentResult } from '@/entities/payment-claim/types';
import type { Subscription } from '@/entities/subscription/types';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { SecondaryButton } from '@/shared/ui/SecondaryButton';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { cn } from '@/shared/lib/cn';
import { haptics } from '@/shared/lib/haptics';
import { useT } from '@/shared/lib/i18n';

type SheetMode = 'confirm' | 'custom' | 'success' | 'pending';

interface MarkPaidSheetProps {
  open: boolean;
  subscription: Subscription | null;
  amountDue: number;
  onClose: () => void;
  onRecorded: (result: RecordPaymentResult) => void;
  onShowBalance?: () => void;
}

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value);
}

function amountLabel(amount: number, currency: string) {
  return `${Math.max(0, amount).toLocaleString('ru')} ${CURRENCY_SYMBOL[currency] ?? currency}`;
}

function TickerBorder({ flip = false }: { flip?: boolean }) {
  const points = Array.from({ length: 101 }, (_, i) => {
    const x = i * 4;
    const y = (i % 2 === 0) === !flip ? 12 : 0;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg height="12" preserveAspectRatio="none" viewBox="0 0 400 12" width="100%" className="block">
      <polygon fill="rgba(255,251,238,0.97)" points={points} />
    </svg>
  );
}

function MiniSubIcon({ sub }: { sub: Subscription }) {
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = sub.icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const isSlug = sub.icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(sub.icon);

  if (isEmoji) {
    return (
      <span className="w-[24px] h-[24px] rounded-[7px] bg-brand-gradient text-white text-[14px] flex items-center justify-center leading-none">
        {sub.icon}
      </span>
    );
  }
  if (isSlug) {
    return (
      <img
        src={`/api/v1/icons/${sub.icon}/default`}
        alt={sub.name}
        className="w-[24px] h-[24px] rounded-[7px] object-contain bg-white/60 p-1"
      />
    );
  }
  return <SubscriptionIcon letter={letter} size="sm" />;
}

function Receipt({ sub, amount }: { sub: Subscription; amount: number }) {
  const t = useT();

  return (
    <div className="mx-[-2px]">
      <TickerBorder />
      <div className="bg-[rgba(255,251,238,0.97)] px-5 py-4 text-center">
        <div className="text-[10px] font-bold text-text-muted tracking-[0.09em] font-mono">
          {t('payment.receiptDebt')}
        </div>
        <MonoAmount
          value={amountLabel(amount, sub.currency)}
          variant="gradient"
          size="xl"
          className="text-[44px] sm:text-[48px]"
        />
        <div className="h-px bg-[repeating-linear-gradient(90deg,rgba(18,52,36,0.18)_0px,rgba(18,52,36,0.18)_4px,transparent_4px,transparent_8px)] my-[9px]" />
        <div className="flex items-center justify-center gap-2 min-w-0">
          <MiniSubIcon sub={sub} />
          <span className="text-[13px] text-text-muted font-mono truncate">
            {sub.name} · {t('common.currentPeriod')}
          </span>
        </div>
      </div>
      <TickerBorder flip />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg fill="none" height="52" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="52">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg fill="none" height="48" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" viewBox="0 0 24 24" width="48">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function ResultCard({
  mode,
  sub,
  result,
  onClose,
  onShowBalance,
}: {
  mode: 'success' | 'pending';
  sub: Subscription;
  result: RecordPaymentResult;
  onClose: () => void;
  onShowBalance?: () => void;
}) {
  const t = useT();
  const isPending = mode === 'pending';
  const amount = amountLabel(toNumber(result.amount), result.currency);

  return (
    <motion.div
      key={mode}
      className={cn(
        'fixed inset-0 z-[70] bg-page-mesh bg-[#eef5ec]',
        'flex flex-col items-center justify-center text-center px-[30px] py-10',
      )}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 240, damping: 25 }}
    >
      <LiquidGlass
        displacementScale={36}
        blurAmount={0.08}
        saturation={130}
        aberrationIntensity={1.2}
        elasticity={0.22}
        cornerRadius={999}
        padding="0"
        overLight
      >
        <div
          className={cn(
            'w-[116px] h-[116px] rounded-full flex items-center justify-center',
            'shadow-[0_20px_48px_rgba(18,120,90,0.32),inset_0_2px_0_rgba(255,255,255,0.4)]',
            isPending
              ? 'bg-[rgba(244,251,245,0.62)] border-2 border-status-warn text-status-warn'
              : 'bg-brand-gradient text-white',
          )}
        >
          {isPending ? <ClockIcon /> : <CheckIcon />}
        </div>
      </LiquidGlass>

      <h1 className="text-[31px] font-bold tracking-[-0.02em] mt-6">
        {isPending ? t('payment.resultPendingTitle') : t('payment.resultSuccessTitle')}
      </h1>
      <div
        className={cn(
          'inline-flex items-center gap-2 text-[13px] font-semibold rounded-full px-[14px] py-[6px] mt-3',
          isPending
            ? 'text-status-warn bg-[rgba(230,103,53,0.15)] border border-[rgba(230,103,53,0.3)]'
            : 'text-status-paid bg-[rgba(0,153,40,0.14)] border border-[rgba(0,153,40,0.3)]',
        )}
      >
        {isPending ? t('payment.pendingBadge') : t('payment.successBadge', { amount })}
      </div>
      <p className="text-[15px] text-text-secondary max-w-[280px] mt-4">
        {isPending
          ? t('payment.pendingText')
          : t('payment.successText', { name: sub.name })}
      </p>

      <div className="w-full max-w-[330px] flex flex-col gap-3 mt-7">
        <SecondaryButton onClick={onShowBalance ?? onClose}>
          {t('payment.showBalance')}
        </SecondaryButton>
        <button
          className="text-[15px] font-semibold text-text-secondary py-2"
          onClick={onClose}
        >
          {t('common.close')}
        </button>
      </div>
    </motion.div>
  );
}

export function MarkPaidSheet({
  open,
  subscription,
  amountDue,
  onClose,
  onRecorded,
  onShowBalance,
}: MarkPaidSheetProps) {
  const t = useT();
  const [mode, setMode] = useState<SheetMode>('confirm');
  const [customAmount, setCustomAmount] = useState('');
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [result, setResult] = useState<RecordPaymentResult | null>(null);

  const parsedCustom = useMemo(() => {
    const normalized = customAmount.replace(',', '.').trim();
    const value = Number(normalized);
    return Number.isFinite(value) ? value : 0;
  }, [customAmount]);

  const mutation = useMutation({
    mutationFn: (amount?: number) => {
      if (!subscription) throw new Error('Subscription is not selected');
      const trimmedComment = comment.trim();
      return paymentApi.record({
        subscription_id: subscription.id,
        amount,
        ...(trimmedComment ? { comment: trimmedComment } : {}),
      });
    },
    onSuccess: (data) => {
      haptics.success();
      setResult(data);
      onRecorded(data);
      setMode(data.ledger_operation_id ? 'success' : 'pending');
    },
    onError: () => {
      haptics.error();
    },
  });

  if (!subscription) return null;

  const fullLabel = amountLabel(amountDue, subscription.currency);
  const canSubmitCustom = parsedCustom > 0;
  const customHint =
    parsedCustom > 0 && parsedCustom < amountDue
      ? t('payment.partialHint', {
          amount: amountLabel(parsedCustom, subscription.currency),
          full: fullLabel,
        })
      : parsedCustom > amountDue
        ? t('payment.overpayHint', {
            amount: amountLabel(parsedCustom, subscription.currency),
            full: fullLabel,
          })
        : null;

  function resetAndClose() {
    onClose();
    window.setTimeout(() => {
      setMode('confirm');
      setCustomAmount('');
      setComment('');
      setShowComment(false);
      setResult(null);
      mutation.reset();
    }, 180);
  }

  function resetAndShowBalance() {
    setMode('confirm');
    setCustomAmount('');
    setComment('');
    setShowComment(false);
    setResult(null);
    mutation.reset();
    onShowBalance?.();
  }

  return (
    <AnimatePresence>
      {open && result && (mode === 'success' || mode === 'pending') && (
        <ResultCard
          mode={mode}
          sub={subscription}
          result={result}
          onClose={resetAndClose}
          onShowBalance={resetAndShowBalance}
        />
      )}

      {open && !result && (
        <motion.div
          className="fixed inset-0 z-[60] bg-[rgba(14,40,28,0.34)] backdrop-blur-[2px] flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={resetAndClose}
        >
          <motion.div
            className="glass-sheet rounded-t-[30px] px-[22px] pt-4 pb-[calc(28px+env(safe-area-inset-bottom))] flex flex-col gap-[14px]"
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="w-[46px] h-[5px] rounded-full bg-text-primary/20 self-center" />

            {mode === 'confirm' && (
              <>
                <h2 className="text-[24px] font-bold tracking-[-0.01em]">{t('payment.confirmTitle')}</h2>
                <Receipt sub={subscription} amount={amountDue} />

                {mutation.isError && (
                  <div className="rounded-[13px] border border-status-debt/25 bg-status-debt/10 px-3 py-2 text-[13px] text-status-debt">
                    {t('payment.submitError')}
                  </div>
                )}

                {showComment && (
                  <textarea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder={t('payment.commentPlaceholder')}
                    className="min-h-[78px] resize-none rounded-[14px] bg-white/45 border border-white/60 px-3 py-3 text-[15px] outline-none focus:border-brand-green/50"
                  />
                )}

                <PrimaryButton
                  loading={mutation.isPending}
                  disabled={amountDue <= 0}
                  onClick={() => mutation.mutate(undefined)}
                  className="py-[16px] text-[17px] rounded-[16px]"
                >
                  {t('payment.paidFull', { amount: fullLabel })}
                </PrimaryButton>
                <SecondaryButton onClick={() => setMode('custom')}>
                  {t('payment.otherAmount')}
                </SecondaryButton>
                <button
                  className="text-center text-[14px] text-text-secondary font-semibold py-1"
                  onClick={() => setShowComment((value) => !value)}
                >
                  {showComment ? t('payment.hideComment') : t('payment.addComment')}
                </button>
              </>
            )}

            {mode === 'custom' && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-[23px] font-bold tracking-[-0.01em]">{t('payment.customTitle')}</h2>
                  <button
                    className="text-[14px] font-semibold text-text-secondary"
                    onClick={() => setMode('confirm')}
                  >
                    {t('common.back')}
                  </button>
                </div>

                <label className="rounded-[16px] px-4 py-3 flex items-baseline justify-center gap-2 bg-white/55 border border-brand-green/40">
                  <input
                    value={customAmount}
                    onChange={(event) => setCustomAmount(event.target.value)}
                    inputMode="decimal"
                    autoFocus
                    placeholder="0"
                    className="min-w-0 w-full max-w-[190px] bg-transparent text-center text-[44px] font-bold tracking-[-0.02em] outline-none"
                  />
                  <span className="text-[20px] text-text-muted">
                    {CURRENCY_SYMBOL[subscription.currency] ?? subscription.currency}
                  </span>
                </label>

                <button
                  className="text-center text-[13px] font-medium text-text-secondary rounded-[11px] px-3 py-[10px] bg-white/50 border border-brand-green/35"
                  onClick={() => setCustomAmount(String(amountDue))}
                >
                  {t('payment.fullAmount', { amount: fullLabel })}
                </button>

                {customHint && (
                  <div className="text-[13px] text-status-warn bg-[rgba(230,103,53,0.13)] border border-[rgba(230,103,53,0.3)] rounded-[12px] px-3 py-[10px]">
                    {customHint}
                  </div>
                )}

                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={t('payment.commentPlaceholder')}
                  className="min-h-[74px] resize-none rounded-[14px] bg-white/42 border border-white/60 px-3 py-3 text-[15px] outline-none focus:border-brand-green/50"
                />

                {mutation.isError && (
                  <div className="rounded-[13px] border border-status-debt/25 bg-status-debt/10 px-3 py-2 text-[13px] text-status-debt">
                    {t('payment.networkError')}
                  </div>
                )}

                <PrimaryButton
                  loading={mutation.isPending}
                  disabled={!canSubmitCustom}
                  onClick={() => mutation.mutate(parsedCustom)}
                  className="py-[15px] text-[17px] rounded-[16px]"
                >
                  {t('payment.confirmAmount', { amount: amountLabel(parsedCustom, subscription.currency) })}
                </PrimaryButton>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
